import styled, { css } from 'styled-components';
import { caption, text2Bold } from '@ardoq/typography';
import {
  BODY_CELL_CONTAINER_HORIZONTAL_MARGIN,
  BODY_CELL_CONTAINER_HORIZONTAL_PADDING,
  BODY_CELL_CONTAINER_VERTICAL_PADDING,
  CARD_SIBLING_GAP,
  COMPONENT_ITEM_HEIGHT,
  CONTEXT_OUTLINE_OUTSET,
  ICON_WIDTH,
  LEVEL_HEADER_CELL_HORIZONTAL_PADDING,
  LEVEL_HEADER_CELL_VERTICAL_PADDING,
  ROW_GROUP_VERTICAL_MARGIN,
} from '../consts';
import { colors, s4, s8 } from '@ardoq/design-tokens';
import {
  CUSTOM_SVG_ICON_SELECTOR,
  NEGATIVE_COLOR_CSS_VARIABLE,
} from '@ardoq/icons';

export const ComponentMatrixViewContainer = styled.div`
  user-select: none;
  & .rotate-90 {
    transform: rotate(90deg);
  }
`;

const levelHeaderCellCss = css`
  background-color: transparent;
  border: none;
  padding: ${LEVEL_HEADER_CELL_VERTICAL_PADDING}px
    ${LEVEL_HEADER_CELL_HORIZONTAL_PADDING}px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const LevelHeaderCell = styled.div`
  ${levelHeaderCellCss}
`;
export const ColumnLevelHeaderCell = styled.div`
  ${levelHeaderCellCss}
  padding: 0;
  text-align: right;
  grid-column: 1;
  z-index: 1;
`;

export const HeaderCell = styled.div`
  background-color: transparent;
  vertical-align: top;
  white-space: nowrap;
  height: 100%;
`;

export const BodyCell = styled.div`
  vertical-align: top;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

/**
 * Gray container wrapping all items in a body cell. Fills the full cell
 * (no outer margin). Padding per Figma spec: 6px vertical, 4px horizontal.
 * Body rows are seamless — row separation in the row-header area is achieved
 * by encoding ROW_GROUP_VERTICAL_MARGIN into the grid track height for
 * root-group boundary rows (see rowBottomGap in RowElementModel).
 * Corners are rounded only at the very top (first row) and bottom (last row)
 * of the full table so the column reads as one continuous rounded rectangle.
 */
// Root-group boundary border is painted as an absolutely-positioned ::after
// 2px stripe centered inside the ROW_GROUP_VERTICAL_MARGIN gap at the bottom
// of the row track (which the body cell stretches into — see the comment on
// "Body rows are seamless" above). A plain `border-bottom` would sit flush
// against the bottom of the cell, i.e. at the bottom of the row gap instead
// of through its middle.
const ROOT_GROUP_BOUNDARY_BORDER_HEIGHT = 2;
const rootGroupBoundaryBorderBottom =
  (ROW_GROUP_VERTICAL_MARGIN - ROOT_GROUP_BOUNDARY_BORDER_HEIGHT) / 2;

export const BodyCellItemsContainer = styled.div<{
  $isFirstRow: boolean;
  $isLastRow: boolean;
  $isRootGroupBoundary: boolean;
}>`
  position: relative;
  background-color: ${colors.grey100};
  border-top-left-radius: ${({ $isFirstRow }) => ($isFirstRow ? '8px' : '0')};
  border-top-right-radius: ${({ $isFirstRow }) => ($isFirstRow ? '8px' : '0')};
  border-bottom-left-radius: ${({ $isLastRow }) => ($isLastRow ? '8px' : '0')};
  border-bottom-right-radius: ${({ $isLastRow }) => ($isLastRow ? '8px' : '0')};
  border-bottom: ${({ $isLastRow, $isRootGroupBoundary }) =>
    $isLastRow || $isRootGroupBoundary
      ? 'none'
      : `1px solid ${colors.grey400}`};
  margin: 0 ${BODY_CELL_CONTAINER_HORIZONTAL_MARGIN}px;
  padding: ${BODY_CELL_CONTAINER_VERTICAL_PADDING}px
    ${BODY_CELL_CONTAINER_HORIZONTAL_PADDING}px;
  box-sizing: border-box;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${CARD_SIBLING_GAP}px;
  overflow: hidden;
  ${({ $isRootGroupBoundary }) =>
    $isRootGroupBoundary &&
    css`
      &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: ${rootGroupBoundaryBorderBottom}px;
        height: ${ROOT_GROUP_BOUNDARY_BORDER_HEIGHT}px;
        background-color: ${colors.grey400};
      }
    `}
`;

export const ComponentLabel = styled.span<{ $isDark: boolean }>`
  color: ${({ $isDark }) =>
    $isDark ? colors.textOnInvertedStrong : colors.textDefault};
  font-weight: normal;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: break-word;
  min-width: 0;
  flex: 1;
`;
export const ListValueLabel = styled.span`
  ${text2Bold};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: normal;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

export const ComponentMatrixListValueHeaderCellContainer = styled.div<{
  $isMinHeight?: boolean;
}>`
  display: flex;
  align-items: ${({ $isMinHeight }) =>
    $isMinHeight === false ? 'flex-start' : 'center'};
  gap: ${s4};
  background-color: ${colors.surfaceDefaultSubtle};
  border: 1px solid ${colors.borderAction};
  border-radius: 8px;
  padding: ${s8};
  overflow: hidden;
  min-width: 0;
  height: 100%;
`;
/**
 * Selection / context outlines per ARD-31426. Stacked with `box-shadow` so
 * block layout (border-radius, padding) is unaffected. Stacking order
 * (first shadow paints closest to the box):
 *   selection only:    1px grey950             — inner radius 7px, outer 8px
 *   context only:      2px grey100 + 3px blue600 — inner 8px, outer 13px
 *   both:              1px grey950 + 1px grey100 + 3px blue600
 *                       — inner 8px, grey 9px, blue outer 13px
 * grey100 matches the BodyCellItemsContainer background, producing a visual
 * gap between the inner edge (block or selection outline) and the
 * 3px context outline. The inner radius drops to 7px when selected (only)
 * so the 1px grey950 outline curves at 8px (Figma 9311:15846); when also
 * context, the inner stays at 8px to preserve the 13px blue outer radius
 * documented in Figma 9329:24090.
 */
type ComponentItemContainerProps = {
  $isSelected?: boolean;
  $isContext?: boolean;
};

const itemOutlineShadow = ({
  $isSelected,
  $isContext,
}: ComponentItemContainerProps) => {
  if ($isSelected && $isContext) {
    return css`
      box-shadow:
        0 0 0 1px ${colors.grey950},
        0 0 0 2px ${colors.grey100},
        0 0 0 5px ${colors.blue600};
    `;
  }
  if ($isSelected) {
    return css`
      box-shadow: 0 0 0 1px ${colors.grey950};
    `;
  }
  if ($isContext) {
    return css`
      box-shadow:
        0 0 0 2px ${colors.grey100},
        0 0 0 5px ${colors.blue600};
    `;
  }
  return null;
};

export const ComponentItemContainer = styled.div<ComponentItemContainerProps>`
  ${itemOutlineShadow}
  /* Selected (non-context) items drop to 7px so the outer edge of the 1px
   * selection outline curves at the original 8px. Context items keep the
   * 8px inner radius so the 3px blue context ring stays at 13px outer
   * (Figma 9311:15846 / 9329:24090). */
  border-radius: ${({ $isSelected, $isContext }) =>
    $isSelected && !$isContext ? 7 : 8}px;
  padding: 4px 8px;
  /* Reserve CONTEXT_OUTLINE_OUTSET on every side of the context item so the
   * box-shadow outline doesn't get clipped by the body cell or overlap its
   * siblings. Only the context item carves out this space — non-context
   * items have no margin so they remain compact. The body cell measurement
   * (see measureBodyCells in measureCells.ts) grows the containing column
   * width and row height by the same amount when an item is context. */
  margin: ${({ $isContext }) => ($isContext ? CONTEXT_OUTLINE_OUTSET : 0)}px
    ${({ $isContext }) => ($isContext ? CONTEXT_OUTLINE_OUTSET : 0)}px;
  min-height: ${COMPONENT_ITEM_HEIGHT}px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  line-height: 20px;
  overflow: hidden;
`;

export const ComponentItemTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`;
export const ComponentLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

export const OtherLabelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
  padding-bottom: 4px;
  min-width: 0;
  width: 100%;
  white-space: normal;
  & > label {
    margin-top: 0;
    min-width: 0;
  }
`;

export const IconContainer = styled.span<{ $isDark: boolean }>`
  width: ${ICON_WIDTH}px;
  height: ${ICON_WIDTH}px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  font-size: ${ICON_WIDTH}px;
  color: ${({ $isDark }) =>
    $isDark ? colors.iconOnInvertedStrong : colors.iconStrong};
  ${NEGATIVE_COLOR_CSS_VARIABLE}: ${({ $isDark }) =>
    $isDark ? colors.transparent0 : colors.white950};

  > ${CUSTOM_SVG_ICON_SELECTOR} {
    max-height: ${ICON_WIDTH}px;
    min-height: ${ICON_WIDTH}px;
    line-height: 1;
  }
`;

export const ComponentMatrixBodyContainer = styled.div`
  display: grid;
  grid-row: 2;
  grid-column: 2;
  overflow: auto;
  .forExport & {
    overflow: visible;
  }
`;

// `DropdownMenuSectionTitle` applies `header5` styling, which uses
// `text-transform: uppercase` + bold. Subtitle text inherits the
// transform but not the explicit weight/size from caption, so we
// re-apply caption and explicitly cancel the transform.
export const SectionSubtitle = styled.div`
  ${caption};
  color: ${colors.textSubtle};
  text-transform: none;
`;
